"use client";

import { Brain, Database, Globe2, PanelsTopLeft } from "lucide-react";
import type { ComponentProps } from "react";

import {
  PluginIcon,
  type PluginIconPlugin,
} from "@/components/content/plugin-icon";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  isMlxPlugin,
  sourceUsageDescription,
  sourceUsageMetric,
  type RunSourceUsage,
} from "@/lib/run-source-usage";
import { cn } from "@/lib/utils";
// Relative on purpose: this file crosses into the marketing build, whose
// narrow path maps do not cover @/components/app/*.
import { TwinLiveMark } from "@/components/app/twin/twin-live-mark";
import { SourceGlyph } from "./source-glyph";

/** One source as a small disc; a row of them overlaps (`-space-x-1.5`). */
export const SOURCE_DISC_CLASS =
  "flex size-5 items-center justify-center overflow-hidden rounded-full border border-border bg-background ring-1 ring-background";

/*
 * A product's logo inside a round disc: whole and centred, at the size that
 * keeps even a square logo inside the circle, so no mark is cropped. Source
 * avatars and entity cards draw every logo this way.
 */
export const DISC_LOGO_CLASS = "size-[70%] object-contain";

/** The Twin's own disc: its rings fill it edge to edge, as in the Twin's avatar. */
export function TwinDiscMark(
  props: Pick<ComponentProps<typeof TwinLiveMark>, "active" | "durationSeconds" | "minActiveSeconds" | "restartKey">,
) {
  return <TwinLiveMark bleed className="size-full" {...props} />;
}

/** A source's mark in its disc: a logo sits whole inside it, an icon at glyph size. */
export function SourceDiscMark({ plugin }: { plugin: PluginIconPlugin }) {
  return (
    <PluginIcon
      plugin={plugin}
      className={plugin.branding?.iconUrl ? DISC_LOGO_CLASS : "size-3"}
    />
  );
}

/**
 * Presentational half of RunSourcesUsed: the "Retrieved with" label and the
 * overlapping source avatars, rendered entirely from props; with `showNames`
 * the sources are named beside their marks, since a row of marks alone says
 * nothing until each is hovered. The container
 * (`run-sources-used.tsx`) computes the usage from run events and resolves the
 * runtime plugin catalogue; surfaces without live data — print, export, the
 * marketing product bridge — pass fixtures instead, and their module graph
 * never reaches the app's data clients.
 */
export function RunSourcesUsedView(props: {
  sources: RunSourceUsage[];
  plugins?: readonly PluginIconPlugin[];
  showNames?: boolean;
  className?: string;
}) {
  if (props.sources.length === 0) return null;
  // MLX's own plugins read by what they hold ("Workspace"), as the usage names them, never by their catalogue name.
  const pluginsBySlug = new Map(
    (props.plugins ?? []).filter((plugin) => !isMlxPlugin(plugin.slug)).map((plugin) => [plugin.slug, plugin]),
  );
  const names = props.sources.map(
    (source) =>
      (source.pluginSlug
        ? pluginsBySlug.get(source.pluginSlug)?.label
        : undefined) ?? source.label,
  );

  return (
    <TooltipProvider delay={150}>
      <div className={cn("flex items-center gap-1.5", props.className)}>
        <span className="text-xs font-medium text-muted-foreground">
          Retrieved with
        </span>
        <div className="flex items-center -space-x-1.5">
          {props.sources.map((source) => {
            const plugin = source.pluginSlug
              ? pluginsBySlug.get(source.pluginSlug)
              : undefined;
            const label = plugin?.label ?? source.label;
            const metric = sourceUsageMetric(source);
            return (
              <Tooltip key={source.key}>
                <TooltipTrigger
                  render={
                    <span
                      role="img"
                      aria-label={`${label}, ${metric}`}
                      className={SOURCE_DISC_CLASS}
                      tabIndex={0}
                    />
                  }
                >
                  {plugin ? (
                    <SourceDiscMark plugin={plugin} />
                  ) : (
                    <RunSourceIcon source={source} />
                  )}
                </TooltipTrigger>
                <TooltipContent>
                  {sourceUsageDescription(source)}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
        {props.showNames ? (
          <span className="min-w-0 truncate text-xs text-muted-foreground">
            {sourceNames(names)}
          </span>
        ) : null}
      </div>
    </TooltipProvider>
  );
}

/** The sources by name, the first three and then how many more. */
function sourceNames(names: string[]): string {
  const shown = names.slice(0, 3).join(", ");
  return names.length > 3 ? `${shown} +${names.length - 3}` : shown;
}

function RunSourceIcon(props: { source: RunSourceUsage }) {
  const className = "size-3 text-muted-foreground";
  switch (props.source.kind) {
    case "web":
      return <Globe2 className={className} />;
    case "warehouse":
      // Data read from a system's tables wears that system's mark (Xero's), as the system itself would.
      return props.source.pluginSlug
        ? <SourceGlyph source={props.source.pluginSlug} className="size-full" iconClassName="size-full" />
        : <Database className={className} />;
    case "plugin":
      return <Database className={className} />;
    case "workspace":
      return <PanelsTopLeft className={className} />;
    case "memory":
      return <Brain className={className} />;
    case "twin":
      return <TwinDiscMark />;
  }
}
